import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { FiSearch, FiBriefcase, FiGlobe, FiPhone, FiMail, FiDollarSign, FiCheck, FiX, FiTrash2 } from 'react-icons/fi';
import Swal from 'sweetalert2';
import { adminApi } from '../../utils/api';
import './AdminPages.css';

const AdminBrandsPage = () => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery(
    ['admin-brands', page, search],
    () => adminApi.getBrands({ page, limit: 50, search }),
    {
      keepPreviousData: true,
      refetchInterval: 5000 // עדכון אוטומטי כל 5 שניות
    }
  );

  const deleteBrandMutation = useMutation(
    (id) => adminApi.deleteBrand(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('admin-brands');
        toast.success('הליד נמחק');
      },
      onError: () => {
        toast.error('שגיאה במחיקת הליד');
      }
    }
  );

  const brands = data?.data?.brands || [];
  const pagination = data?.data?.pagination || {};

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('he-IL');
  };

  const formatBudget = (budget) => {
    if (!budget) return '-';
    const budgetLabels = {
      'under5k': 'עד 5,000 ₪',
      '5k-10k': '5,000-10,000 ₪',
      '10k-20k': '10,000-20,000 ₪',
      'over20k': 'מעל 20,000 ₪'
    };
    return budgetLabels[budget] || budget;
  };

  const handleDelete = async (brandId) => {
    const result = await Swal.fire({
      title: 'מחיקת ליד',
      text: 'האם אתה בטוח שברצונך למחוק ליד זה?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'כן, מחק',
      cancelButtonText: 'ביטול',
      reverseButtons: true
    });

    if (result.isConfirmed) {
      deleteBrandMutation.mutate(brandId);
    }
  };

  return (
    <div className="admin-page">
      <header className="admin-header">
        <div className="header-content">
          <h1>לידים (מותגים)</h1>
          <span className="total-count">{pagination.total || 0} לידים</span>
        </div>
      </header>

      {/* Toolbar */}
      <div className="admin-toolbar">
        <form onSubmit={handleSearch} className="search-form">
          <div className="search-input-wrapper">
            <FiSearch className="search-icon" />
            <input
              type="text"
              placeholder="חיפוש לפי שם, אימייל, חברה..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <button type="submit" className="btn btn-primary">חפש</button>
        </form>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="loading-state">
          <div className="spinner" />
          <p>טוען נתונים...</p>
        </div>
      ) : error ? (
        <div className="error-state">
          <p>שגיאה בטעינת הנתונים</p>
        </div>
      ) : brands.length === 0 ? (
        <div className="empty-state">
          <FiBriefcase className="empty-icon" />
          <p>לא נמצאו לידים</p>
        </div>
      ) : (
        <div className="table-container">
          <table className="admin-table">
            <thead>
              <tr>
                <th>שם מלא</th>
                <th>טלפון</th>
                <th>דוא"ל</th>
                <th>שם החברה</th>
                <th>כתובת אתר</th>
                <th>פעילות קודמת</th>
                <th>תקציב חודשי</th>
                <th>תאריך הרשמה</th>
                <th>מחיקה</th>
              </tr>
            </thead>
            <tbody>
              {brands.map((brand) => (
                <tr key={brand.id}>
                  <td data-label="שם מלא">
                    <div className="user-cell">
                      <div className="user-avatar-small">
                        <FiBriefcase />
                      </div>
                      <span>{brand.full_name}</span>
                    </div>
                  </td>
                  <td data-label="טלפון">
                    {brand.phone ? (
                      <a href={`tel:${brand.phone}`} className="phone-link">
                        <FiPhone className="cell-icon" />
                        {brand.phone}
                      </a>
                    ) : '-'}
                  </td>
                  <td data-label="דוא״ל">
                    <a href={`mailto:${brand.email}`} className="email-link">
                      <FiMail className="cell-icon" />
                      {brand.email}
                    </a>
                  </td>
                  <td data-label="שם החברה">
                    {brand.company_name || '-'}
                  </td>
                  <td data-label="אתר">
                    {brand.website ? (
                      <a href={brand.website} target="_blank" rel="noopener noreferrer" className="website-link">
                        <FiGlobe className="cell-icon" />
                        לאתר
                      </a>
                    ) : '-'}
                  </td>
                  <td data-label="פעילות קודמת">
                    {brand.had_influencer_activity ? (
                      <span className="badge badge-success"><FiCheck /> כן</span>
                    ) : (
                      <span className="badge badge-secondary"><FiX /> לא</span>
                    )}
                  </td>
                  <td data-label="תקציב חודשי">
                    {brand.monthly_budget ? (
                      <span className="budget-badge">
                        <FiDollarSign />
                        {formatBudget(brand.monthly_budget)}
                      </span>
                    ) : '-'}
                  </td>
                  <td data-label="תאריך הרשמה">{formatDate(brand.created_at)}</td>
                  <td data-label="מחיקה">
                    <button
                      className="btn-icon btn-danger"
                      onClick={() => handleDelete(brand.id)}
                      title="מחק ליד"
                    >
                      <FiTrash2 />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="pagination">
          <button
            className="btn btn-outline"
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
          >
            הקודם
          </button>
          <span className="page-info">
            עמוד {page} מתוך {pagination.pages}
          </span>
          <button
            className="btn btn-outline"
            disabled={page === pagination.pages}
            onClick={() => setPage(page + 1)}
          >
            הבא
          </button>
        </div>
      )}
    </div>
  );
};

export default AdminBrandsPage;
